import * as html from 'smg/html';

import { UUID } from 'smg/uuid';

// outlineSVG creates a new svg with new ids
function outlineSVG() {
    const id = UUID();
    return {
        id,
        svg: `<svg
        version="1.1"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:svg="http://www.w3.org/2000/svg">
        <defs>
            <linearGradient id="linearGradient1320-${id}">
            <stop style="stop-color:#00ffff;stop-opacity:1" offset="0" />
            <stop style="stop-color:#03CCF2;stop-opacity:1" offset="1" />
            </linearGradient>
            <linearGradient
            xlink:href="#linearGradient1320-${id}"
            id="linearGradient1322-${id}"
            gradientUnits="userSpaceOnUse" />
            <clipPath id="clip-top-left-${id}">
            <rect />
            </clipPath>
            <clipPath id="clip-top-right-${id}">
            <rect />
            </clipPath>
            <clipPath id="clip-bottom-right-${id}">
            <rect />
            </clipPath>
            <clipPath id="clip-bottom-left-${id}">
            <rect />
            </clipPath>
        </defs>
        <rect id="outline-top-left-${id}" style="fill:none;stroke:url(#linearGradient1322-${id});stroke-linecap:butt;stroke-linejoin:round;stroke-miterlimit:0;stroke-dasharray:none;stroke-opacity:1;stop-color:#000000" clip-path="url(#clip-top-left-${id})" />
        <rect id="outline-top-right-${id}" style="fill:none;stroke:url(#linearGradient1322-${id});stroke-linecap:butt;stroke-linejoin:round;stroke-miterlimit:0;stroke-dasharray:none;stroke-opacity:1;stop-color:#000000" clip-path="url(#clip-top-right-${id})" />
        <rect id="outline-bottom-right-${id}" style="fill:none;stroke:url(#linearGradient1322-${id});stroke-linecap:butt;stroke-linejoin:round;stroke-miterlimit:0;stroke-dasharray:none;stroke-opacity:1;stop-color:#000000" clip-path="url(#clip-bottom-right-${id})" />
        <rect id="outline-bottom-left-${id}" style="fill:none;stroke:url(#linearGradient1322-${id});stroke-linecap:butt;stroke-linejoin:round;stroke-miterlimit:0;stroke-dasharray:none;stroke-opacity:1;stop-color:#000000" clip-path="url(#clip-bottom-left-${id})" />
        </svg>` };
}

interface borderRadii {
    readonly topLeft: number;
    readonly topRight: number;
    readonly bottomRight: number;
    readonly bottomLeft: number;
}

function calculateAdjustedBorderRadii(
    width: number,
    height: number,
    borderRadii: borderRadii,
): borderRadii {
    // Extract border radii
    const { topLeft, topRight, bottomRight, bottomLeft } = borderRadii;

    // Calculate sum for each side
    const sumTop = topLeft + topRight;
    const sumBottom = bottomLeft + bottomRight;
    const sumLeft = topLeft + bottomLeft;
    const sumRight = topRight + bottomRight;

    // Calculate length values
    const lengthTop = width;
    const lengthBottom = width;
    const lengthLeft = height;
    const lengthRight = height;

    // Compute ratio for each side
    const ratioTop = lengthTop / sumTop;
    const ratioBottom = lengthBottom / sumBottom;
    const ratioLeft = lengthLeft / sumLeft;
    const ratioRight = lengthRight / sumRight;

    // Find the minimum ratio
    const min = Math.min(ratioTop, ratioBottom, ratioLeft, ratioRight);

    // If min < 1, scale the radii
    if (min < 1) {
        return {
            topLeft: topLeft * min,
            topRight: topRight * min,
            bottomRight: bottomRight * min,
            bottomLeft: bottomLeft * min,
        };
    } else {
        // No adjustment needed if min >= 1
        return borderRadii;
    }
}

function borderRadiusXY(radius: string, width: number, height: number): { x: number; y: number } {
    const [rx, ry] = radius.split('/').filter(s => s.trim());

    return {
        x: borderRadiusToPX(rx, width),
        y: borderRadiusToPX(ry || rx, height),
    };
}

function borderRadiusToPX(radius: string, size: number): number {
    if (radius?.endsWith('%')) {
        return size * (parseFloat(radius) / 100);
    } else if (radius?.endsWith('rem')) {
        return parseFloat(radius) * parseFloat(getComputedStyle(document.documentElement).fontSize);
    }
    return parseFloat(radius);
}

function targetElements($target: HTMLElement) {
    if (!($target instanceof HTMLElement && $target.matches('.product-type-radio *,.product-size-radio *,.input-group *,.form-control,label.form-control *,.form-select,.product-type-card,.product-type-card *'))) {
        return;
    }

    const $inputGroup = $target.closest<HTMLElement>('.input-group') || $target.closest<HTMLElement>('label.form-control') ||
        $target.closest<HTMLElement>('.product-type-radio') || $target.closest<HTMLElement>('.product-size-radio') || $target.closest<HTMLElement>('.product-type-card');
    if ($inputGroup) {
        $target = $inputGroup;
    }

    let $input: HTMLElement = $target;
    let $firstInputGroupChild: HTMLElement | null;
    let $lastInputGroupChild: HTMLElement | null;
    if ($target.matches('.input-group') || $target.matches('.product-type-radio') || $target.matches('.product-size-radio')) {
        const $maybeInput = $target.querySelector<HTMLElement>('.form-control,.form-select');
        if (!$maybeInput) {
            return;
        }
        const $inputGroupParent = $target.closest<HTMLElement>('.input-group-parent');
        $input = $maybeInput;
        let $children: NodeListOf<HTMLElement>;
        if ($inputGroupParent) {
            $children = $inputGroupParent.querySelectorAll<HTMLElement>('.form-control,.form-select,.input-group-text,.btn');
        } else {
            $children = $target.querySelectorAll<HTMLElement>('.form-control,.form-select,.input-group-text,.btn');
        }
        $firstInputGroupChild = $children[0];
        $lastInputGroupChild = $children[$children.length - 1];
    } else if ($target.matches('.product-type-card') || $target.matches('.product-type-card *')) {
        $input = $target;
        $firstInputGroupChild = $input;
        $lastInputGroupChild = $input;
    } else if ($target.matches('label.form-control') && $target.querySelector('input[type="radio"],input[type="checkbox"]')) {
        $input = $target.querySelector('input[type="radio"],input[type="checkbox"]') as HTMLElement;
        $firstInputGroupChild = $input;
        $lastInputGroupChild = $input;
    } else {
        $firstInputGroupChild = $input;
        $lastInputGroupChild = $input;
    }
    return { $target, $input, $firstInputGroupChild, $lastInputGroupChild };
}

function createBorder($target: HTMLElement, radiusVar: string) {
    const { id, svg } = outlineSVG();
    const $border = html.FromString(`<div>${svg}</div>`);
    $border.className = 'form-control-outline';
    $border.style.zIndex = '6';

    const $svg = $border.querySelector('svg') as SVGSVGElement;
    const $rectTopLeft = $border.querySelector(`#outline-top-left-${id}`) as SVGRectElement;
    const $rectTopRight = $border.querySelector(`#outline-top-right-${id}`) as SVGRectElement;
    const $rectBottomRight = $border.querySelector(`#outline-bottom-right-${id}`) as SVGRectElement;
    const $rectBottomLeft = $border.querySelector(`#outline-bottom-left-${id}`) as SVGRectElement;
    const $rects = [$rectTopLeft, $rectTopRight, $rectBottomRight, $rectBottomLeft];
    const $gradient = $border.querySelector(`#linearGradient1322-${id}`) as SVGLinearGradientElement;

    const $clipTopLeft = $border.querySelector(`#clip-top-left-${id} rect`) as SVGRectElement;
    const $clipTopRight = $border.querySelector(`#clip-top-right-${id} rect`) as SVGRectElement;
    const $clipBottomRight = $border.querySelector(`#clip-bottom-right-${id} rect`) as SVGRectElement;
    const $clipBottomLeft = $border.querySelector(`#clip-bottom-left-${id} rect`) as SVGRectElement;

    const draw = () => {
        const outlineWidth = parseFloat(getComputedStyle(document.body).getPropertyValue(radiusVar));
        const borderWidth = parseFloat(getComputedStyle($target).borderTopWidth);
        const addlBorderRadius = (outlineWidth / 2) - borderWidth;

        const { width, height } = $target.getBoundingClientRect();
        const svgWidth = width + outlineWidth * 2;
        const svgHeight = height + outlineWidth * 2;

        $svg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
        $svg.setAttribute('width', String(svgWidth));
        $svg.setAttribute('height', String(svgHeight));

        $rects.forEach($rect => {
            $rect.setAttribute('width', String(width - (borderWidth * 2) + outlineWidth));
            $rect.setAttribute('height', String(height - (borderWidth * 2) + outlineWidth));
            $rect.setAttribute('x', String(outlineWidth / 2));
            $rect.setAttribute('y', String(outlineWidth / 2));
        });

        const borderTopLeftRadius = getComputedStyle($target).borderTopLeftRadius;
        const tlxy = borderRadiusXY(borderTopLeftRadius, $target.offsetWidth, $target.offsetHeight);
        $rectTopLeft.setAttribute('rx', String(calculateAdjustedBorderRadii($rectTopLeft.width.baseVal.value, $rectTopLeft.height.baseVal.value / 2, { topLeft: tlxy.x + addlBorderRadius, topRight: 0, bottomRight: 0, bottomLeft: 0 }).topLeft));
        $rectTopLeft.setAttribute('ry', String(calculateAdjustedBorderRadii($rectTopLeft.width.baseVal.value, $rectTopLeft.height.baseVal.value / 2, { topLeft: tlxy.x + addlBorderRadius, topRight: 0, bottomRight: 0, bottomLeft: 0 }).topLeft));
        $rectTopLeft.style.strokeWidth = String(outlineWidth);

        const borderTopRightRadius = getComputedStyle($target).borderTopRightRadius;
        const trxy = borderRadiusXY(borderTopRightRadius, $target.offsetWidth, $target.offsetHeight);
        $rectTopRight.setAttribute('rx', String(calculateAdjustedBorderRadii($rectTopRight.width.baseVal.value, $rectTopRight.height.baseVal.value / 2, { topLeft: tlxy.x + addlBorderRadius, topRight: trxy.x + addlBorderRadius, bottomRight: 0, bottomLeft: 0 }).topRight));
        $rectTopRight.setAttribute('ry', String(calculateAdjustedBorderRadii($rectTopRight.width.baseVal.value, $rectTopRight.height.baseVal.value / 2, { topLeft: tlxy.x + addlBorderRadius, topRight: trxy.x + addlBorderRadius, bottomRight: 0, bottomLeft: 0 }).topRight));
        $rectTopRight.style.strokeWidth = String(outlineWidth);

        const borderBottomRightRadius = getComputedStyle($target).borderBottomRightRadius;
        const brxy = borderRadiusXY(borderBottomRightRadius, $target.offsetWidth, $target.offsetHeight);
        $rectBottomRight.setAttribute('rx', String(calculateAdjustedBorderRadii($rectBottomRight.width.baseVal.value, $rectBottomRight.height.baseVal.value / 2, { topLeft: 0, topRight: 0, bottomRight: brxy.x + addlBorderRadius, bottomLeft: brxy.x + addlBorderRadius }).bottomRight));
        $rectBottomRight.setAttribute('ry', String(calculateAdjustedBorderRadii($rectBottomRight.width.baseVal.value, $rectBottomRight.height.baseVal.value / 2, { topLeft: 0, topRight: 0, bottomRight: brxy.x + addlBorderRadius, bottomLeft: brxy.x + addlBorderRadius }).bottomRight));
        $rectBottomRight.style.strokeWidth = String(outlineWidth);

        const borderBottomLeftRadius = getComputedStyle($target).borderBottomLeftRadius;
        const blxy = borderRadiusXY(borderBottomLeftRadius, $target.offsetWidth, $target.offsetHeight);
        $rectBottomLeft.setAttribute('rx', String(calculateAdjustedBorderRadii($rectBottomLeft.width.baseVal.value, $rectBottomLeft.height.baseVal.value / 2, { topLeft: 0, topRight: 0, bottomRight: blxy.x + addlBorderRadius, bottomLeft: blxy.x + addlBorderRadius }).bottomLeft));
        $rectBottomLeft.setAttribute('ry', String(calculateAdjustedBorderRadii($rectBottomLeft.width.baseVal.value, $rectBottomLeft.height.baseVal.value / 2, { topLeft: 0, topRight: 0, bottomRight: blxy.x + addlBorderRadius, bottomLeft: blxy.x + addlBorderRadius }).bottomLeft));
        $rectBottomLeft.style.strokeWidth = String(outlineWidth);

        $gradient.setAttribute('x1', String(0));
        $gradient.setAttribute('y1', String(0));
        $gradient.setAttribute('x2', String(width));
        $gradient.setAttribute('y2', String(height));

        $clipTopLeft.setAttribute('x', '0');
        $clipTopLeft.setAttribute('y', '0');
        $clipTopLeft.setAttribute('width', String(svgWidth / 2));
        $clipTopLeft.setAttribute('height', String(svgHeight / 2));

        $clipTopRight.setAttribute('x', String(svgWidth / 2));
        $clipTopRight.setAttribute('y', '0');
        $clipTopRight.setAttribute('width', String(svgWidth / 2));
        $clipTopRight.setAttribute('height', String(svgHeight / 2));

        $clipBottomRight.setAttribute('x', String(svgWidth / 2));
        $clipBottomRight.setAttribute('y', String(svgHeight / 2));
        $clipBottomRight.setAttribute('width', String(svgWidth / 2));
        $clipBottomRight.setAttribute('height', String(svgHeight / 2));

        $clipBottomLeft.setAttribute('x', '0');
        $clipBottomLeft.setAttribute('y', String(svgHeight / 2));
        $clipBottomLeft.setAttribute('width', String(svgWidth / 2));
        $clipBottomLeft.setAttribute('height', String(svgHeight / 2));

        $border.style.top = `${$target.offsetTop - (outlineWidth - borderWidth)}px`;
        $border.style.left = `${$target.offsetLeft - (outlineWidth - borderWidth)}px`;
    };
    draw();

    let cancel = false;
    const sizeAndPosition = { width: $target.offsetWidth, height: $target.offsetHeight, top: $target.offsetTop, left: $target.offsetLeft };
    const checkSizeAndPosition = () => {
        if (cancel) {
            return;
        }
        if (sizeAndPosition.width !== $target.offsetWidth ||
            sizeAndPosition.height !== $target.offsetHeight ||
            sizeAndPosition.top !== $target.offsetTop ||
            sizeAndPosition.left !== $target.offsetLeft) {
            // update the size and position of the border
            sizeAndPosition.width = $target.offsetWidth;
            sizeAndPosition.height = $target.offsetHeight;
            sizeAndPosition.top = $target.offsetTop;
            sizeAndPosition.left = $target.offsetLeft;
            draw();
        }
        window.requestAnimationFrame(checkSizeAndPosition);
    };
    window.requestAnimationFrame(checkSizeAndPosition);

    $target.parentElement?.appendChild($border);

    setTimeout(() => $border.style.opacity = '1');

    const fadeOutTimeout = getComputedStyle(document.body).getPropertyValue('--smg-focus-ring-animation-speed');

    const removeBorder = () => {
        $border.style.opacity = '0';
        setTimeout(() => {
            $border.remove();
            cancel = true;
        }, parseFloat(fadeOutTimeout) * 1000);
    };
    return removeBorder;
}

document.addEventListener('mouseover', e => {
    const { $target, $input, $firstInputGroupChild, $lastInputGroupChild } = targetElements(e.target as HTMLElement) || {};
    if (!$target || !$input || !$firstInputGroupChild || !$lastInputGroupChild) {
        return;
    }

    if ($input.matches('.border-0') || $input.matches(':disabled') || $input.matches(':focus') || (!$input.matches('.product-type-card') && !$input.matches('.product-type-card *') && ($input.matches(':read-only') && !$input.matches('.form-select') && !$input.matches('[type="radio"]')))) {
        return;
    }

    const removeBorder = createBorder($target, '--smg-hover-ring-width');
    const removeBorderAndListeners = () => {
        removeBorder();
        $target.removeEventListener('mouseover', removeBorderAndListeners);
        $input?.removeEventListener('focus', removeBorderAndListeners);
    };

    $target.addEventListener('mouseleave', removeBorderAndListeners);
    $input?.addEventListener('focus', removeBorderAndListeners);
});

document.addEventListener('focus', e => {
    const { $target, $input, $firstInputGroupChild, $lastInputGroupChild } = targetElements(e.target as HTMLElement) || {};
    if (!$target || !$input || !$firstInputGroupChild || !$lastInputGroupChild) {
        return;
    }

    if ($input.matches('.border-0') || $input.matches(':disabled') || ($input.matches(':read-only') && !$input.matches('.form-select') && !$input.matches('[type="radio"]'))) {
        return;
    }

    const removeBorder = createBorder($target, '--smg-focus-ring-width');
    const removeBorderAndListeners = () => {
        removeBorder();
        $input?.removeEventListener('blur', removeBorderAndListeners);
    };

    $input?.addEventListener('blur', removeBorderAndListeners);
}, true);

// handle hidden input radios and checkboxes inside of label.form-controls
document.addEventListener('click', e => {
    const { $target, $input } = targetElements(e.target as HTMLElement) || {};
    if ($target?.matches('label.form-control') && $input?.matches('input[type="radio"],input[type="checkbox"]')) {
        e.stopPropagation();

        if ($input.matches('[type="radio"]')) {
            const $otherRadios = Array.from(document.querySelectorAll<HTMLInputElement>(`input[type="radio"][name="${CSS.escape($input.getAttribute('name') ?? '')}"]`)).filter($radio => $radio !== $input);

            const removeBorder = createBorder($target, '--smg-focus-ring-width');
            const removeBorderAndListeners = () => {
                removeBorder();
                $otherRadios.forEach($radio => $radio.removeEventListener('click', removeBorderAndListeners));
            };

            $otherRadios.forEach($radio => $radio.addEventListener('click', removeBorderAndListeners));
        } else if ($input.matches('[type="checkbox"]:checked')) {
            // console.log($input, ($input as HTMLInputElement).checked);
            const removeBorder = createBorder($target, '--smg-focus-ring-width');
            const removeBorderAndListeners = () => {
                removeBorder();
                $input.removeEventListener('click', removeBorderAndListeners);
            };

            $input.addEventListener('click', removeBorderAndListeners, { once: true });
        }
    }
});

export function RadioBorderListener() {
    let lastRemoveBorder: (() => void) | undefined = undefined;
    const $radios = Array.from(document.querySelectorAll<HTMLInputElement>('input[type="radio"]'));
    for (const $radio of $radios) {
        $radio.addEventListener('change', () => {
            const $target = $radio.closest<HTMLElement>('.product-type-radio') || $radio.closest('.product-size-radio') || $radio.closest<HTMLElement>('.input-group');
            const $input = $radio;

            if (!$target || !$input) {
                return;
            }

            lastRemoveBorder?.();
            if ($input.checked) {
                lastRemoveBorder = createBorder($target, '--smg-focus-ring-width');
            } else {
                lastRemoveBorder = undefined;
            }
        });
    }
}
